import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, FileText, Trash2, Plus, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ReferenceDocsPanel — the persistent library view.
 *
 * Rendered on the Dashboard as a sidebar card. Handles:
 *   • Listing the user's reference documents
 *   • Uploading new documents (PDF / DOCX / TXT / MD)
 *   • Renaming and deleting
 *
 * Kept as a single self-contained component instead of split across files
 * because it's only used in one place (Dashboard) and stays under ~200 lines.
 */
export const ReferenceDocsPanel = () => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const authHeader = () => {
    const t = localStorage.getItem('sage_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reference-documents`, {
        headers: authHeader(),
      });
      setDocs(data.documents || []);
    } catch (err) {
      // Non-fatal; empty state is a valid rendering.
      if (process.env.NODE_ENV !== 'production') console.debug('fetchDocs failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      // No `title` — server derives from filename; user can rename after.
      await axios.post(`${API}/reference-documents`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${file.name} added to library`);
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/reference-documents/${doc.doc_id}`, {
        headers: authHeader(),
      });
      toast.success('Document removed');
      setDocs((prev) => prev.filter((d) => d.doc_id !== doc.doc_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleRename = async (doc) => {
    const next = window.prompt('New title', doc.title);
    if (!next || !next.trim() || next.trim() === doc.title) return;
    try {
      await axios.patch(
        `${API}/reference-documents/${doc.doc_id}`,
        { title: next.trim() },
        { headers: authHeader() },
      );
      setDocs((prev) =>
        prev.map((d) => (d.doc_id === doc.doc_id ? { ...d, title: next.trim() } : d)),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    }
  };

  return (
    <div
      className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6"
      data-testid="reference-docs-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3
            className="text-lg font-light text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Reference Documents
          </h3>
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
            {docs.length} in library
          </p>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="frost-button rounded-sm px-3 py-1.5 text-xs disabled:opacity-50"
          data-testid="reference-docs-upload-button"
          aria-label="Upload reference document"
        >
          {uploading ? 'Uploading…' : (
            <>
              <Upload className="w-3.5 h-3.5" />
              Upload
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          data-testid="reference-docs-file-input"
        />
      </div>

      <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
        Concrete artifacts SAGE can discuss — manuscripts, notes, ideas.
        Attach any of these to a coaching session from Live Coaching.
      </p>

      {loading ? (
        <p className="text-xs text-zinc-600 py-4 text-center">Loading library…</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-zinc-600 py-6 text-center italic">
          No documents yet. Upload a PDF, DOCX, or text file to start.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="reference-docs-list">
          {docs.map((doc) => (
            <li
              key={doc.doc_id}
              className="group flex items-center gap-3 px-3 py-2 bg-white/[0.02] border border-white/5 hover:border-white/15 rounded-sm transition-colors"
              data-testid={`reference-doc-row-${doc.doc_id}`}
            >
              <FileText className="w-3.5 h-3.5 text-zinc-500 shrink-0" aria-hidden="true" />
              <button
                type="button"
                onClick={() => handleRename(doc)}
                className="flex-1 min-w-0 text-left text-sm text-zinc-200 truncate hover:text-white transition-colors"
                title="Click to rename"
              >
                {doc.title}
              </button>
              <span className="text-[10px] font-mono text-zinc-600 uppercase">
                {doc.kind}
              </span>
              <button
                type="button"
                onClick={() => handleDelete(doc)}
                className="text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                aria-label={`Delete ${doc.title}`}
                data-testid={`reference-doc-delete-${doc.doc_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * AttachDocsSheet — modal picker used by the LiveCoaching paperclip.
 *
 * Two actions in one sheet:
 *   1. Upload a NEW file (auto-attaches to `sessionId` on success)
 *   2. Pick existing documents from the library (attaches selected)
 *
 * When it closes we call `onChange(newAttachedIds)` so the parent can
 * refresh the chip row without re-fetching from the server.
 */
export const AttachDocsSheet = ({
  open,
  onClose,
  sessionId,
  attachedIds,
  onChange,
}) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = useRef(null);

  const authHeader = () => {
    const t = localStorage.getItem('sage_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // Reset local selection state each time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reference-documents`, {
          headers: authHeader(),
        });
        setDocs(data.documents || []);
      } catch {
        toast.error('Could not load your library');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUpload = async (file) => {
    if (!file || !sessionId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: created } = await axios.post(
        `${API}/reference-documents`,
        form,
        {
          headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
        },
      );
      // Immediately attach the freshly-uploaded doc to this session.
      await axios.post(
        `${API}/coaching/session/${sessionId}/attach`,
        { doc_ids: [created.doc_id] },
        { headers: authHeader() },
      );
      toast.success(`${created.title} attached`);
      onChange([...(attachedIds || []), created.doc_id]);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAttachSelected = async () => {
    if (!sessionId || selected.size === 0) {
      onClose();
      return;
    }
    try {
      const idsToAttach = Array.from(selected);
      await axios.post(
        `${API}/coaching/session/${sessionId}/attach`,
        { doc_ids: idsToAttach },
        { headers: authHeader() },
      );
      toast.success(
        `${idsToAttach.length} document${idsToAttach.length === 1 ? '' : 's'} attached`,
      );
      onChange([...new Set([...(attachedIds || []), ...idsToAttach])]);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attach failed');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9997] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="attach-docs-sheet"
    >
      <div
        className="bg-stone-950 border border-white/15 rounded-sm max-w-lg w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3
            className="text-xl font-light text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Attach reference material
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="attach-docs-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-xs text-zinc-400 mb-5 leading-relaxed">
            SAGE will read these for context during this session. They stay
            in your library for future sessions too.
          </p>

          {/* Upload row */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full frost-button rounded-sm px-4 py-3 mb-5 text-sm disabled:opacity-50"
            data-testid="attach-docs-upload-new"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading & attaching…' : 'Upload a new document'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.md"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />

          {/* Library picker */}
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
            Or pick from your library
          </div>
          {loading ? (
            <p className="text-xs text-zinc-600 py-4 text-center">Loading…</p>
          ) : docs.length === 0 ? (
            <p className="text-xs text-zinc-600 py-6 text-center italic">
              No documents in your library yet — upload one above.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="attach-docs-library">
              {docs.map((doc) => {
                const alreadyAttached = (attachedIds || []).includes(doc.doc_id);
                const isSelected = selected.has(doc.doc_id);
                return (
                  <li key={doc.doc_id}>
                    <button
                      type="button"
                      onClick={() => !alreadyAttached && toggleSelected(doc.doc_id)}
                      disabled={alreadyAttached}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm border transition-colors ${
                        alreadyAttached
                          ? 'bg-white/[0.03] border-emerald-400/25 text-emerald-200/70 cursor-default'
                          : isSelected
                          ? 'bg-white/[0.05] border-white/40 text-white'
                          : 'bg-white/[0.02] border-white/10 hover:border-white/25 text-zinc-200'
                      }`}
                      data-testid={`attach-docs-item-${doc.doc_id}`}
                    >
                      <FileText className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      <span className="flex-1 text-left text-sm truncate">
                        {doc.title}
                      </span>
                      <span className="text-[10px] font-mono uppercase text-zinc-500">
                        {doc.kind}
                      </span>
                      {alreadyAttached ? (
                        <Check className="w-3.5 h-3.5 text-emerald-400" aria-label="Already attached" />
                      ) : isSelected ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Plus className="w-3.5 h-3.5 text-zinc-500" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-zinc-500 hover:text-zinc-300 px-3 py-1.5"
            data-testid="attach-docs-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAttachSelected}
            disabled={selected.size === 0}
            className="threshold-button rounded-sm px-4 py-1.5 text-sm disabled:opacity-40"
            data-testid="attach-docs-confirm"
          >
            <span>
              Attach{selected.size > 0 ? ` (${selected.size})` : ''}
            </span>
            <span className="threshold-button-arrow" aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceDocsPanel;
